import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../OJTCoordinator/OJTCoordinator.css';
import '../SupervisorHome.css';
import './AttendanceApproval.css';
import { FaUser, FaUsers, FaComments, FaClipboardCheck, FaCheck, FaTimes } from 'react-icons/fa';
import { FaLocationDot, FaArrowRightFromBracket } from 'react-icons/fa6';
import { useSupervisorAuth } from '../../../lib/AuthProvider';

// Static data for demonstration
const staticPendingRequests = [
  {
    id: '1',
    studentName: 'John Lloyd Micua',
    studentId: '22-03323',
    requestType: 'Time In',
    requestedTime: '8:00 AM',
    requestedDate: '2025-12-04',
    reason: 'Forgot to time in due to emergency meeting',
    submittedAt: '2025-12-04 10:30 AM'
  },
  {
    id: '2',
    studentName: 'Jan Rameses Paz',
    studentId: '22-02022',
    requestType: 'Time Out',
    requestedTime: '5:00 PM',
    requestedDate: '2025-12-03',
    reason: 'Phone battery died before time out',
    submittedAt: '2025-12-04 09:15 AM'
  },
  {
    id: '3',
    studentName: 'Mark Adrian Dumlao',
    studentId: '22-03325',
    requestType: 'Time In',
    requestedTime: '1:00 PM',
    requestedDate: '2025-12-04',
    reason: 'App crashed during PM time in',
    submittedAt: '2025-12-04 02:45 PM'
  }
];

const AttendanceApproval: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(staticPendingRequests);

  const { signOut } = useSupervisorAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleApprove = (requestId: string) => {
    setPendingRequests(pendingRequests.filter(req => req.id !== requestId));
  };

  const handleReject = (requestId: string) => {
    setPendingRequests(pendingRequests.filter(req => req.id !== requestId));
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="supervisor-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <button 
              onClick={() => navigate('/supervisor/home')} 
              className={`nav-link ${location.pathname === '/supervisor/home' ? 'active' : ''}`}
            >
              <FaUsers className="nav-icon" /> My Trainees
            </button>
            <button 
              onClick={() => navigate('/supervisor/attendance')} 
              className={`nav-link ${location.pathname === '/supervisor/attendance' ? 'active' : ''}`}
            >
              <FaClipboardCheck className="nav-icon" /> Attendance
            </button>
            <button 
              onClick={() => navigate('/supervisor/feedback')} 
              className={`nav-link ${location.pathname === '/supervisor/feedback' ? 'active' : ''}`}
            >
              <FaComments className="nav-icon" /> Feedback
            </button>
            <button 
              onClick={() => navigate('/supervisor/profile')} 
              className={`nav-link ${location.pathname === '/supervisor/profile' ? 'active' : ''}`}
            >
              <FaUser className="nav-icon" /> Profile
            </button>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="supervisor-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={(e) => {
          if ((e.target as HTMLElement).closest('.sidebar-logout-button')) {
            return;
          }
          setIsMenuOpen(false);
        }}>
          <button 
            onClick={() => navigate('/supervisor/home')} 
            className="nav-link"
          >
            <FaUsers className="nav-icon" /> My Trainees
          </button>
          <button 
            onClick={() => navigate('/supervisor/attendance')} 
            className="nav-link"
          >
            <FaClipboardCheck className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/supervisor/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/supervisor/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
        </nav>
        <div className="sidebar-footer">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }} 
            className="sidebar-logout-button"
          >
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="attendance-approval-section">
          <div className="attendance-header">
            <h3 className="coordinator-title">
              <FaClipboardCheck style={{ marginRight: '8px' }} />
              Attendance Approval
            </h3>
            <p className="attendance-subtitle">Approve or reject manual time in/out requests from trainees</p>
          </div>

          {pendingRequests.length > 0 ? (
            <div className="attendance-requests-container">
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Trainee</th>
                    <th>Student ID</th>
                    <th>Request Type</th>
                    <th>Date & Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.studentName}</td>
                      <td>{request.studentId}</td>
                      <td>
                        <span className={`request-type-badge ${request.requestType.includes('In') ? 'time-in' : 'time-out'}`}>
                          {request.requestType}
                        </span>
                      </td>
                      <td>
                        <div className="date-time-cell">
                          <span className="date">{request.requestedDate}</span>
                          <span className="time">{request.requestedTime}</span>
                        </div>
                      </td>
                      <td>
                        <div className="approval-action-buttons">
                          <button 
                            className="approve-btn"
                            onClick={() => handleApprove(request.id)}
                            title="Approve Request"
                          >
                            <FaCheck /> Approve
                          </button>
                          <button 
                            className="reject-btn"
                            onClick={() => handleReject(request.id)}
                            title="Reject Request"
                          >
                            <FaTimes /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <FaClipboardCheck size={48} color="#ccc" />
              <h3>No Pending Requests</h3>
              <p>All attendance requests have been processed.</p>
              <p>New requests will appear here when trainees submit them.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AttendanceApproval;
